import type { PatternPreset } from '~/types/presets';
import type { DevicePattern } from '~/types/midi';
import musicData from '~/data/musicData';
import { PATTERNS, type PatternParts } from '~/types/pattern';

/**
 * FM Synth Default Pattern
 *
 * A sparse, contemplative pattern in G# Minor Pentatonic with quarter note phrasing.
 * Perfect for FM synthesis with its wide dynamic range and space for complex timbres to breathe.
 *
 * Musical characteristics:
 * - Key: G# Minor Pentatonic (G#, B, C#, D#, F#)
 * - Structure: AABAAC (6-part form with variation)
 * - Note Length: Quarter notes (4n)
 * - Density: Sparse (30% space probability)
 * - Range: 2 octaves (24 semitones) for melodic exploration
 * - Dissonance: 0% (strictly pentatonic for consonant FM timbres)
 *
 * Pattern parts:
 * - A: Minimalist phrase with rests, ending on B3
 * - B: Descending bass movement (F#3 → F#2 → C#3 → D#2)
 * - C: High register phrase starting at G#4
 */

const defaultFMPattern: DevicePattern = {
  rootNote: musicData.rootNotes.find(n => n.note === 'G♯') || musicData.rootNotes[0],
  scale: musicData.scales.find(s => s.id === 'pentatonic-min') || musicData.scales[0],
  scaleMode: 'Minor',
  pattern: PATTERNS.find(p => p.name === 'AABAAC') || PATTERNS[0],
  patternParts: {
    A: [
      { note: null, duration: '4n' },
      { note: null, duration: '4n' },
      { note: null, duration: '4n' },
      { note: 'B3', duration: '4n' },
    ],
    B: [
      { note: 'F♯3', duration: '4n' },
      { note: 'F♯2', duration: '4n' },
      { note: 'C♯3', duration: '4n' },
      { note: 'D♯2', duration: '4n' },
    ],
    C: [
      { note: 'G♯4', duration: '4n' },
      { note: 'D♯3', duration: '4n' },
      { note: 'F♯3', duration: '4n' },
      { note: 'F♯3', duration: '4n' },
    ],
    D: [
      { note: 'B2', duration: '4n' },
      { note: 'G♯3', duration: '4n' },
      { note: null, duration: '4n' },
      { note: 'D♯3', duration: '4n' },
    ],
  } as PatternParts,
  beatsPerBar: 4,
  tempo: 110,
  swing: 0,
  space: 30,
  spaceMin: 20,
  spaceMax: 50,
  dissonance: 0,
  dissonanceMin: 0,
  dissonanceMax: 2,
  spread: 12,
  spreadMin: 0,
  spreadMax: 24,
  range: 24,
  rangeMin: 12,
  rangeMax: 37,
  scrambleToggles: {
    scrambleStructure: true,
    scrambleRootNote: true,
    scrambleMode: true,
    scrambleScale: true,
    scrambleSpace: true,
    scrambleDissonance: true,
    scrambleSpread: true,
    scrambleRange: true,
  },
};

/**
 * FM Synth Default Pattern Preset
 */
export const DEFAULT_FM_PATTERN_PRESET: PatternPreset = {
  id: 'default-fm-pattern',
  name: 'Default',
  pattern: defaultFMPattern,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  userId: null, // System preset
  isPublic: true,
};

/**
 * Get the FM Synth default pattern preset
 * Returns a deep copy to avoid mutation
 */
export function getDefaultFMPattern(): PatternPreset {
  return {
    ...DEFAULT_FM_PATTERN_PRESET,
    pattern: {
      ...DEFAULT_FM_PATTERN_PRESET.pattern,
      rootNote: { ...DEFAULT_FM_PATTERN_PRESET.pattern.rootNote },
      scale: { ...DEFAULT_FM_PATTERN_PRESET.pattern.scale },
      pattern: { ...DEFAULT_FM_PATTERN_PRESET.pattern.pattern },
      patternParts: JSON.parse(JSON.stringify(DEFAULT_FM_PATTERN_PRESET.pattern.patternParts)),
      scrambleToggles: { ...DEFAULT_FM_PATTERN_PRESET.pattern.scrambleToggles },
    },
  };
}
